import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getOrders, getOrderVendorsStatus } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { 
  Loader2, 
  PackageCheck, 
  ArrowRight, 
  CheckCircle, 
  Clock, 
  Truck,
  ChevronDown,
  ChevronUp
} from "lucide-react";

export default function FinalizedOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [ordersVendorStatus, setOrdersVendorStatus] = useState({});
  const [expandedOrders, setExpandedOrders] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      // Get both finalized and partially_received orders
      const [finalizedRes, partialRes] = await Promise.all([
        getOrders("finalized"),
        getOrders("partially_received")
      ]);
      
      const allOrders = [...finalizedRes.data, ...partialRes.data].sort((a, b) => 
        new Date(b.finalized_at || b.created_at) - new Date(a.finalized_at || a.created_at)
      );
      setOrders(allOrders);
      
      // Load vendor status for each order
      const statusMap = {};
      for (const order of allOrders) {
        try {
          const statusRes = await getOrderVendorsStatus(order.id);
          statusMap[order.id] = statusRes.data;
        } catch (e) {
          console.error("Error loading vendor status:", e);
        }
      }
      setOrdersVendorStatus(statusMap);
      
      // Auto-expand first order if only one
      if (allOrders.length === 1) {
        setExpandedOrders({ [allOrders[0].id]: true });
      }
    } catch (error) {
      toast.error("Failed to load orders");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const toggleOrder = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const getUniqueVendorCount = (items) => {
    return new Set(items.map(item => item.vendor_name)).size;
  };

  const getPendingVendorCount = (orderId) => {
    const status = ordersVendorStatus[orderId];
    if (!status) return 0;
    return status.vendors.filter(v => !v.is_received).length;
  };

  return (
    <AppLayout title="Receive Orders" showBack>
      <div className="p-4 md:p-6 max-w-2xl mx-auto">
        <p className="text-sm text-stone-500 mb-4">
          Select an order, then tap a vendor to record their delivery.
        </p>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-kale-600" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <PackageCheck className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No orders awaiting delivery</p>
            <p className="text-xs text-stone-400 mt-1">
              Orders will appear here after admin finalizes them
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order, index) => {
              const isExpanded = expandedOrders[order.id];
              const vendorStatus = ordersVendorStatus[order.id];
              const pendingCount = getPendingVendorCount(order.id);
              const isPartial = order.status === "partially_received";
              
              return (
                <Card 
                  key={order.id} 
                  className={`overflow-hidden ${isPartial ? 'border-amber-200' : 'border-blue-200'}`}
                  data-testid={`order-${order.id}`}
                >
                  {/* Order Header - Clickable */}
                  <div 
                    className={`p-4 cursor-pointer ${isPartial ? 'bg-amber-50' : 'bg-blue-50'}`}
                    onClick={() => toggleOrder(order.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge 
                            variant="secondary" 
                            className={isPartial ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}
                          >
                            {isPartial ? (
                              <>
                                <Clock className="w-3 h-3 mr-1" />
                                Partial ({pendingCount} pending)
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Ready
                              </>
                            )}
                          </Badge>
                        </div>
                        <p className="text-sm font-semibold text-stone-800">
                          {order.order_name || `Order #${order.id.substring(0, 8)}`}
                        </p>
                        <p className="text-sm text-stone-600">
                          <span className="font-medium">{order.items.length}</span> items • {" "}
                          <span className="font-medium">{getUniqueVendorCount(order.items)}</span> vendors
                        </p>
                        <p className="text-xs text-stone-400 mt-1">
                          Finalized: {formatDate(order.finalized_at || order.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-stone-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-stone-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Vendor List */}
                  {isExpanded && vendorStatus && (
                    <CardContent className="p-0 border-t">
                      <div className="divide-y">
                        {vendorStatus.vendors.map((vendor) => (
                          <div 
                            key={vendor.vendor_name}
                            className={`p-4 ${vendor.is_received ? 'bg-stone-50' : 'hover:bg-stone-50 cursor-pointer'}`}
                            onClick={() => {
                              if (!vendor.is_received) {
                                navigate(`/orders/${order.id}/receive/${encodeURIComponent(vendor.vendor_name)}`);
                              }
                            }}
                            data-testid={`vendor-${vendor.vendor_name}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  vendor.is_received ? 'bg-kale-100' : 'bg-blue-100'
                                }`}>
                                  {vendor.is_received ? (
                                    <CheckCircle className="w-5 h-5 text-kale-700" />
                                  ) : (
                                    <Truck className="w-5 h-5 text-blue-700" />
                                  )}
                                </div>
                                <div>
                                  <p className={`font-medium ${vendor.is_received ? 'text-stone-500' : 'text-stone-900'}`}>
                                    {vendor.vendor_name}
                                  </p>
                                  <p className="text-xs text-stone-500">
                                    {vendor.item_count} items
                                    {vendor.is_received && vendor.received_info && (
                                      <span className="ml-2 text-kale-600">
                                        ✓ Received {formatDate(vendor.received_info.received_at)}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              {!vendor.is_received && (
                                <ArrowRight className="w-5 h-5 text-blue-400" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
